package ru.villawonka.app.presentation.screens.search.compose

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import cafe.adriel.voyager.core.screen.ScreenKey
import cafe.adriel.voyager.core.screen.uniqueScreenKey
import cafe.adriel.voyager.koin.koinNavigatorScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.navigator.bottomSheet.BottomSheetNavigator
import cafe.adriel.voyager.navigator.bottomSheet.LocalBottomSheetNavigator
import cafe.adriel.voyager.navigator.tab.Tab
import cafe.adriel.voyager.navigator.tab.TabOptions
import org.jetbrains.compose.resources.painterResource
import ru.villawonka.app.domain.model.toBase64
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.presentation.screens.search.compose.layout.SearchRootLayout
import ru.villawonka.app.presentation.screens.search.model.SearchCategoryUIModel
import ru.villawonka.app.presentation.screens.search.model.toVariantPickerItemUIModel
import ru.villawonka.app.presentation.screens.search.viewmodel.SearchUIEvent
import ru.villawonka.app.presentation.screens.search.viewmodel.SearchViewModel
import ru.villawonka.app.presentation.screens.searchFilter.compose.SearchFilterScreen
import ru.villawonka.app.presentation.screens.settings.compose.SettingsScreen
import ru.villawonka.app.presentation.screens.variantPicker.compose.VariantPickerScreen
import ru.villawonka.app.resources.AppResources
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_search_24

object SearchScreen : Tab {

    override val key: ScreenKey = uniqueScreenKey

    override val options: TabOptions
        @Composable
        get() = TabOptions(
            index = 0u,
            title = AppResources.strings.bottomNavSearch,
            icon = painterResource(Res.drawable.ic_search_24)
        )

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.current?.parent!!
        val bottomSheetNavigator = LocalBottomSheetNavigator.current

        val viewModel = navigator.koinNavigatorScreenModel<SearchViewModel>()
        val searchState by viewModel.searchUIState.collectAsStateWithLifecycle()
        val searchResultsState by viewModel.searchResultUIState.collectAsStateWithLifecycle()

        registerUIEventListener(viewModel, navigator, bottomSheetNavigator)

        navigator.saveableState(key) {
            SearchRootLayout(
                searchState = searchState,
                searchResultsState = searchResultsState,
                viewModel = viewModel,
                callbacks = rememberSaveable {
                    SearchCallbacks(
                        onMapPinButtonClick = viewModel::onMapPinButtonClick,
                        onSettingsButtonClick = viewModel::onSettingsButtonClick,
                        onTabSelected = viewModel::onTabSelected,
                        onSearchSortButtonClick = viewModel::onSearchSortButtonClicked,
                        onSearchFilterButtonClick = viewModel::onSearchFilterButtonClicked,
                        onRetryButtonClick = viewModel::onRetryButtonClick,
                        onLoadNextPage = viewModel::onLoadNextPage,
                        onLoadSimilar = viewModel::onLoadSimilar
                    )
                },
            )
        }
    }

    @Composable
    private fun registerUIEventListener(
        viewModel: SearchViewModel,
        navigator: Navigator,
        bottomSheetNavigator: BottomSheetNavigator,
    ) {
        LaunchedEffect(Unit) {
            viewModel.searchUIEvents.collect { event ->
                when (event) {
                    is SearchUIEvent.ShowSortScreen ->
                        bottomSheetNavigator.show(
                            screen = VariantPickerScreen(
                                input = VariantPickerScreen.Params.Input(
                                    title = AppResources.strings.searchSortScreenTitle,
                                    selectedItem = event.selectedItem.toVariantPickerItemUIModel(),
                                    items = event.items.map { it.toVariantPickerItemUIModel() },
                                )
                            ) { outputParams ->
                                viewModel.onSearchSortScreenResult(outputParams)
                            }
                        )

                    is SearchUIEvent.ShowFilterScreen ->
                        navigator.push(
                            item = SearchFilterScreen(
                                inputParams = SearchFilterScreen.Params.Input(event.searchParamsDomain),
                            ) { outputParams ->
                                println(outputParams.searchParamsDomain.toBase64())
                                viewModel.onSearchFilterScreenResult(outputParams)
                            }
                        )

                    is SearchUIEvent.ScrollToUp -> Unit
                    is SearchUIEvent.OpenCountrySelectScreen -> Unit
                    is SearchUIEvent.OpenSettingsScreen -> navigator.push(SettingsScreen())
                }
            }
        }
    }
}

data class SearchCallbacks(
    val onMapPinButtonClick: () -> Unit,
    val onSettingsButtonClick: () -> Unit,
    val onTabSelected: (SearchCategoryUIModel) -> Unit,
    val onSearchFilterButtonClick: () -> Unit,
    val onSearchSortButtonClick: () -> Unit,
    val onRetryButtonClick: () -> Unit,
    val onLoadSimilar: (PostUIModel) -> Unit,
    val onLoadNextPage: suspend () -> Unit,
)